import {
  customElement,
  property,
  queryAssignedNodes,
  state,
  query,
} from "lit/decorators.js";
import { html, LitElement, TemplateResult } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref, Ref } from "lit/directives/ref.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { Highlightable, TypeaheadOption } from "@slhuicore/base";
import { SlhTextfield, TextfieldConstants } from "@slhui/textfield";
import { SlhFormElement } from "@slhui/base/form";
import { SlhFormIcons } from "@slhui/form-icons";
import {
  TEXTFIELD_TYPEAHEAD_ITEM_SELECT,
  TEXTFIELD_TYPEAHEAD_ITEM_CLICK,
} from "@slhui/textfield-typeahead-item";
import { style } from "./assets/textfield-typeahead.generated-style";

/**
 * Textfield typeahed component
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-color-neutral-shade-00 - Typeahead dropdown background color.
 * @cssprop --slh-color-neutral-shade-80 - Font color.
 * @cssprop --slh-color-neutral-shade-10 - Scroll color.
 * @cssprop --slh-color-neutral-shade-20 - Scroll background.
 * @cssprop --slh-color-neutral-shade-40 - Scroll hover color.
 * @cssprop --slh-color-primary - Loader color.
 * @cssprop --slh-color-neutral-shade-20 - Animation background color. Loader background color.
 * @cssprop --slh-base-bold-font-weight - Typeahead highlight font weight. Typeahead hint font weight.
 * @cssprop --slh-color-neutral - Typeahead highlight font color.
 * @cssprop --slh-color-neutral-shade-60 - Typeahead hint color.
 * @cssprop --slh--base-xxs-font-size - Typeahead hint font size.
 * @cssprop --slh-base-xxs-line-height - Typeahead hint line height.
 *
 * @slot default - Optional label above the input field.
 * @slot hint - Custom content below the typeahead textfield, designed to contain recommendations for user input.
 */
@customElement("slh-textfield-typeahead")
export default class SlhTextfieldTypeahead
  extends Highlightable(LitElement)
  implements SlhFormElement
{
  /**
   * Optional label typeahead textfield field
   */
  @queryAssignedNodes()
  protected defaultSlotNodes: Array<HTMLElement> | undefined;

  @queryAssignedNodes({ slot: "hint" })
  protected hintSlotNodes: Array<HTMLElement> | undefined;

  @query(".hint-icon-container", false)
  private hintIconContainer!: HTMLElement;

  /**
   * Assign name attribute to the typeahead
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  name: string = "";

  /**
   * Assign value to the typeahead
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  value?: string;

  /**
   * Assign placeholder text to the typeahead
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  placeholder?: string;

  /**
   * Disables the typeahead
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  disabled: boolean = false;

  /**
   * Sets the typeahead as non-editable
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  readonly: boolean = false;

  /**
   * Sets the typeahead in error state
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  error: boolean = false;

  /**
   * Sets the options for typeahead
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  options: TypeaheadOption[] = [];

  /**
   * Aria label value
   */
  @property({
    type: String,
    attribute: "aria-label",
    reflect: true,
  })
  ariaLabel: string = "";

  private textfieldRef: Ref<SlhTextfield> = createRef();

  private typeaheadRef: Ref<HTMLElement> = createRef();

  @state()
  private typeaheadOptions: TypeaheadOption[] = [];

  @state()
  private showLoading: boolean = false;

  @state()
  private state: string = "";

  @state()
  protected open: boolean = false;

  @state()
  protected hasHint: boolean = false;

  onOutsideClick: (this: Document, e: MouseEvent) => void = () => {};

  onInput: (e: Event) => void = () => {};

  onItemSelect: (e: Event) => void = () => {};

  static styles = style;

  connectedCallback() {
    this.state = this.value ?? "";
    this.onOutsideClick = this.outsideClick.bind(this);
    this.onInput = this.onTextfieldChange.bind(this);
    this.onItemSelect = this.itemSelect.bind(this);
    super.connectedCallback();
  }

  firstUpdated() {
    this.textfieldRef.value!.addEventListener(
      TextfieldConstants.EVENT_TEXTFIELD_INPUT,
      this.onInput
    );
    this.addEventListener(TEXTFIELD_TYPEAHEAD_ITEM_CLICK, this.onItemSelect);

    this.defaultSlotNodes?.forEach((element) => {
      if (element.nodeName === "SLH-LABEL") {
        this.typeaheadRef.value!.classList.add("has-label");
      }
    });
  }

  /**
   * Requests an update which is processed asynchronously.
   *
   * @param name name of requesting property
   * @param oldValue old value of requesting property
   */
  requestUpdate(name?: PropertyKey, oldValue?: unknown) {
    if (name === "options") {
      this.setOptions();
    }

    return super.requestUpdate(name, oldValue);
  }

  /**
   * remove all event listeners
   */
  disconnectedCallback() {
    this.textfieldRef?.value?.removeEventListener(
      TextfieldConstants.EVENT_TEXTFIELD_INPUT,
      this.onInput
    );
    document.removeEventListener("click", this.onOutsideClick);
  }

  /**
   * @returns {boolean}
   */
  public isOpen(): boolean {
    return this.open;
  }

  /**
   * Toggle typeahead
   */
  public toggle() {
    if (!this.disabled) {
      if (this.open) {
        this.hideItems();
      } else {
        this.showItems();
      }
    }
  }

  /**
   * Open typeahead items
   */
  public showItems() {
    if (!this.disabled) {
      document.addEventListener("click", this.onOutsideClick);
      this.open = true;
      this.typeaheadRef.value!.style.display = "block";
    }
  }

  /**
   * Close typeahead items
   */
  public hideItems() {
    if (!this.disabled) {
      document.removeEventListener("click", this.onOutsideClick);
      this.open = false;
      this.hideTypeahead();
    }
  }

  /**
   * On textfield input get value and show loader
   */
  onTextfieldChange(event: Event): void {
    event.stopPropagation();

    this.state = this.textfieldRef.value!.getValue() as string;
    this.showLoading = true;

    if (event instanceof CustomEvent) {
      const customEvent = new CustomEvent(
        TextfieldConstants.EVENT_TEXTFIELD_INPUT,
        {
          detail: {
            value: this.state,
          },
          bubbles: true,
          composed: true,
        }
      );
      this.dispatchEvent(customEvent);
    }
  }

  /**
   * remove event listener and close typeahead dropdown
   */
  hideTypeahead(): void {
    this.typeaheadRef.value!.style.display = "none";
    this.showLoading = false;
  }

  /**
   * clear the textfield
   */
  resetValue() {
    this.textfieldRef.value!.setAttribute("value", "");
    this.value = undefined;
    this.state = "";
  }

  /**
   * display options
   */
  private setOptions() {
    this.showLoading = false;
    this.typeaheadOptions = this.options;
  }

  /**
   * Close typeahead on outside click
   *
   * @param {MouseEvent} event
   */
  private outsideClick(event: MouseEvent) {
    if (!event.composedPath().includes(this.renderRoot)) {
      this.hideItems();
    }
  }

  /**
   * return label of the typeahead textfield
   * @returns {string}
   */
  public getLabel(): string {
    return this.textfieldRef.value!.getLabel();
  }

  /**
   * return entered value
   * @returns {string}
   */
  getValue(): string {
    return this.state;
  }

  /**
   * Renders textfield with properties
   * @returns {TemplateResult}
   */
  private renderTextfield(): TemplateResult {
    return html`<slh-textfield
      ${ref(this.textfieldRef)}
      type="${TextfieldConstants.InputType.TEXT}"
      name="${ifDefined(this.name)}"
      placeholder="${ifDefined(this.placeholder)}"
      value="${this.state}"
      ?disabled="${this.disabled}"
      ?readonly="${this.readonly}"
      ?error="${this.error}"
    ></slh-textfield>`;
  }

  /**
   * on item click
   * @param {Event} e
   */
  private itemSelect(e: Event) {
    if (e instanceof CustomEvent && e.detail.value!) {
      this.state = e.detail.text;
      // this.value = e.detail.value;
      this.textfieldRef.value!.setAttribute("value", "");
      const customEvent = new CustomEvent(TEXTFIELD_TYPEAHEAD_ITEM_SELECT, {
        detail: {
          value: { value: e.detail.value, label: e.detail.text },
          id: this.id,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(customEvent);
      this.hideTypeahead();
    }
  }

  /**
   * Renders typeahed items
   * @returns {TemplateResult}
   */
  private renderTypeaheadOptions() {
    const options: TemplateResult<1>[] = [];
    if (this.typeaheadOptions.length) {
      this.typeaheadRef.value!.classList.remove("loading");
      this.typeaheadOptions.forEach((option: any) => {
        const label = this.renderHighlight(option.label, this.state);
        options.push(
          html`<slh-textfield-typeahead-item
            value="${option.value}"
            text="${option.label}"
            .innerHTML="${label}"
          ></slh-textfield-typeahead-item>`
        );
      });
      return options;
    }
    return html``;
  }

  /**
   * display loader
   * @returns {TemplateResult}
   */
  private showLoadingAnimation(): TemplateResult {
    this.typeaheadRef.value!.classList.add("loading");
    return html`<div class="loader"></div>`;
  }

  /**
   * Renders typeahead holder
   * @returns {TemplateResult}
   */
  private renderTypeahead() {
    return html`<div
      class="typeahead-options-wrapper"
      ${ref(this.typeaheadRef)}
    >
      ${this.showLoading
        ? this.showLoadingAnimation()
        : this.renderTypeaheadOptions()}
    </div>`;
  }

  /**
   * Render component template
   * @returns {TemplateResult}
   */
  render(): TemplateResult {
    return html`<div class="textfield-typeahead-wrapper">
      <slot></slot>
      <div class="textfield" @click="${this.toggle}">
        ${this.renderTextfield()}
      </div>
      ${this.renderHint()} ${this.renderTypeahead()}
    </div>`;
  }

  /**
   * Returns input name
   * @returns {string}
   */
  public getName(): string {
    return this.name;
  }

  public clear() {
    this.state = "";
  }

  /**
   * Returns HTML native element
   * @returns {HTMLInputElement}
   */
  public getNativeElement(): HTMLInputElement {
    return this.textfieldRef.value!.getNativeElement();
  }

  // eslint-disable-next-line class-methods-use-this
  public hasErrorSlot(): boolean {
    return true;
  }

  /**
   * Render textfield hint
   * @returns {TemplateResult}
   */
  private renderHint(): TemplateResult {
    return html`<div class="hint-wrapper ${classMap(this.renderHintClasses())}">
      <div class="hint-icon-container"></div>
      <slot name="hint" @slotchange="${this.onHintSlotChange}"></slot>
    </div>`;
  }

  private onHintSlotChange(): void {
    this.hasHint = false;
    this.hintSlotNodes?.forEach((hint) => {
      if (hint.textContent?.trim() !== "") {
        this.hasHint = true;
      }
    });
    if (this.hasHint) {
      this.hintIconContainer?.appendChild(this.getHintIcon());
    } else {
      this.hintIconContainer.innerHTML = "";
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private getHintIcon(): HTMLElement {
    return SlhFormIcons.getHintIcon();
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  private renderHintClasses(): ClassInfo {
    return {
      hidden: !this.hasHint,
    };
  }
}
